/**
 * App background — источник правды для пользовательского цвета фона интерфейса.
 *
 * Чисто клиентская настройка (как в проекте «Цветы»): выбранный светлый оттенок
 * хранится в localStorage и применяется к `<html>` через CSS-переменную
 * `--app-bg-base` + атрибут `data-app-bg="custom"`. Производные поверхности
 * (карточки, вторичные фоны, рамки) считаются в CSS через `color-mix()` —
 * см. блок `:root[data-app-bg="custom"]` в `src/index.css`.
 *
 * Ключ хранения глобальный (не per-user) намеренно: anti-FOUC скрипт в
 * `index.html` читает значение ДО загрузки React/auth, когда `userId` ещё
 * неизвестен. Фон — настройка устройства/браузера, как `sidebar-collapsed`.
 *
 * ВАЖНО: значение пишется как «сырой» hex (без JSON), чтобы инлайн-скрипт мог
 * прочитать его простой регуляркой. Логика этого модуля и инлайн-скрипта в
 * `index.html` должны оставаться согласованными.
 */

export const STORAGE_KEY = 'toss-app-bg';
export const ROOT_ATTR = 'data-app-bg';
export const BASE_VAR = '--app-bg-base';

/**
 * Дефолтный фон = текущая канва toss (`--color-bg-app`). Он же «сброс».
 *
 * 26.08.2026 сменился с `#fdfdfc` на `#f6f6f7` вместе с канвой всего приложения. Три места
 * обязаны держать одно значение, иначе анти-FOUC скрипт и React разойдутся в первом кадре:
 * эта константа, пресет `default` ниже и сравнение в инлайн-скрипте `index.html`.
 *
 * Уже сохранённые пользователями цвета правка не трогает: {@link writeStored} удаляет ключ,
 * когда значение совпадает с дефолтом, поэтому `#fdfdfc` ни у кого не лежит в хранилище —
 * прежний фон вернулся отдельным пресетом «Почти белый».
 */
export const DEFAULT_BG = '#f6f6f7';

/** Порог относительной яркости: всё темнее — осветляем, чтобы текст читался. */
const LIGHT_THRESHOLD = 0.6;

export interface BgPreset {
  /** Стабильный slug для key/aria. */
  id: string;
  /** Hex цвета канвы. */
  hex: string;
  /** Подпись для UI. */
  label: string;
}

/**
 * Светлые нейтральные пресеты. Первый — дефолт toss (он же «Стандартный»/сброс).
 * Все заведомо светлые → тёмный текст остаётся контрастным (WCAG).
 */
export const BG_PRESETS: BgPreset[] = [
  { id: 'default', hex: '#f6f6f7', label: 'Стандартный' },
  // Канва портала до 26.08.2026. Оставлена пресетом, а не удалена: кто-то к ней привык.
  { id: 'paper', hex: '#fdfdfc', label: 'Почти белый' },
  { id: 'snow', hex: '#fafafa', label: 'Снежный' },
  { id: 'gray', hex: '#f4f4f3', label: 'Светло-серый' },
  { id: 'cool', hex: '#eef0f3', label: 'Прохладный' },
  { id: 'warm', hex: '#f4f2ef', label: 'Тёплый' },
  { id: 'silver', hex: '#e9eaec', label: 'Серебристый' },
];

const HEX_RE = /^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/;

/**
 * Нормализует произвольный ввод к `#rrggbb` в нижнем регистре или `null`.
 * Принимает 3- и 6-значные hex с/без `#`.
 */
export function normalizeHex(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  const match = HEX_RE.exec(input.trim());
  if (!match) return null;
  let hex = match[1].toLowerCase();
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${hex}`;
}

function srgbChannelToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Относительная яркость по WCAG (0 — чёрный, 1 — белый). `hex` должен быть нормализован. */
export function relativeLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (
    0.2126 * srgbChannelToLinear(r) +
    0.7152 * srgbChannelToLinear(g) +
    0.0722 * srgbChannelToLinear(b)
  );
}

function mixWithWhite(hex: string, whiteRatio: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (channel: number): string =>
    Math.round(channel * (1 - whiteRatio) + 255 * whiteRatio)
      .toString(16)
      .padStart(2, '0');
  return `#${mix(r)}${mix(g)}${mix(b)}`;
}

/**
 * Гарантирует светлый результат: нормализует hex и, если он слишком тёмный
 * (например, попал из испорченного localStorage), осветляет к белому. Возвращает
 * `null` для невалидного ввода.
 */
export function normalizeToLight(hex: unknown): string | null {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  if (relativeLuminance(normalized) >= LIGHT_THRESHOLD) return normalized;
  return mixWithWhite(normalized, 0.8);
}

/**
 * Применяет фон к `<html>`. Дефолт/невалид → сброс к стандартному.
 * Возвращает фактически применённый цвет (`DEFAULT_BG` при сбросе).
 */
export function applyBackground(hex: unknown): string {
  const normalized = normalizeToLight(hex);
  if (!normalized || normalized === DEFAULT_BG) {
    resetBackground();
    return DEFAULT_BG;
  }
  const root = document.documentElement;
  root.style.setProperty(BASE_VAR, normalized);
  root.setAttribute(ROOT_ATTR, 'custom');
  return normalized;
}

/** Возвращает фон к стандартному: снимает переменную и атрибут с `<html>`. */
export function resetBackground(): void {
  const root = document.documentElement;
  root.style.removeProperty(BASE_VAR);
  root.removeAttribute(ROOT_ATTR);
}

/** Читает сохранённый цвет из localStorage (или `null`). */
export function readStored(): string | null {
  try {
    return normalizeHex(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/** Пишет цвет в localStorage. Дефолт/`null` → удаляет ключ. */
export function writeStored(hex: string | null): void {
  try {
    const normalized = normalizeHex(hex);
    if (!normalized || normalized === DEFAULT_BG) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, normalized);
    }
  } catch {
    // private mode / quota — best-effort
  }
}
